import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../contexts/ThemeContext';

const { FiAlertTriangle, FiX } = FiIcons;

const ConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = 'Confirm Action',
  message = 'Are you sure you want to continue?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary' // 'primary', 'danger', 'warning'
}) => {
  const { theme } = useTheme();

  const getConfirmButtonClasses = () => {
    const base = 'px-4 py-2 rounded-lg transition-colors font-medium';
    
    switch (confirmVariant) {
      case 'danger':
        return `${base} bg-red-600 text-white hover:bg-red-700`;
      case 'warning':
        return `${base} bg-yellow-600 text-white hover:bg-yellow-700`;
      case 'primary':
      default:
        return `${base} bg-blue-600 text-white hover:bg-blue-700`;
    }
  };

  const getIconColor = () => {
    switch (confirmVariant) {
      case 'danger':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      case 'primary':
      default:
        return 'text-blue-500';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className={`relative w-full max-w-md rounded-lg shadow-xl ${
              theme === 'dark' 
                ? 'bg-slate-800 text-white' 
                : 'bg-white text-gray-900'
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center space-x-3">
                <SafeIcon 
                  icon={FiAlertTriangle} 
                  className={`w-6 h-6 ${getIconColor()}`} 
                />
                <h2 className="text-lg font-semibold">{title}</h2>
              </div>
              <button
                onClick={onClose}
                className={`p-1.5 rounded-lg transition-colors ${
                  theme === 'dark' 
                    ? 'hover:bg-slate-700' 
                    : 'hover:bg-gray-100'
                }`}
                aria-label="Close modal"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className={`text-sm leading-relaxed ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {message}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 p-6 pt-0">
              <button
                onClick={onClose}
                className={`px-4 py-2 border rounded-lg transition-colors ${
                  theme === 'dark' 
                    ? 'border-slate-600 hover:bg-slate-700' 
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {cancelText}
              </button>
              <button
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
                className={getConfirmButtonClasses()}
              >
                {confirmText}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmModal;